import React from 'react';
import { Button, Modal } from '@nsi-advisory-services/components';
import { UI, Section, Note, List, Chapter } from '../shared/helpModalParts';

// A walkthrough of the View Hours page, written against the actual controls:
// every named thing below is a field, button or column that exists on screen,
// so the reader can match what they are told to what they are looking at.
//
// `canSelectOthers` mirrors the page's own rule for who may change the employee
// on screen, so the chapter about reading someone else's timesheet is only
// shown to people who can actually do it.
export default function ViewHoursHelpModal({ open, onClose, canSelectOthers = false, nonExempt = false }) {
  const chapters = [
    {
      key: 'pick',
      label: 'Choosing what you are looking at',
      icon: 'bi-funnel',
      body: (
        <>
          <Section title="The filter bar">
            Everything on the page describes one person over one stretch of time,
            and the bar across the top is where both are chosen.
            <List>
              <li>
                <UI>Pay Period</UI> which semi-monthly period to report on. The
                current one is marked <UI>Current</UI> in the list.
              </li>
              <li>
                <UI>End Pay Period (optional)</UI> leave it empty for a single
                period. Setting it reports on everything from the start period
                through the end one.
              </li>
              <li>
                <UI>Refresh Hours</UI> pulls the latest entries from TimeCamp.
                The page reads cached data, so anything logged in the last few
                minutes may not be there until you press it.
              </li>
            </List>
          </Section>

          <Section title="Your figures come from TimeCamp">
            Nothing here is entered by hand. Hours, tasks, descriptions and
            breaks are all read from what is logged in TimeCamp.<br></br> To make changes, correct the entry in TimeCamp and
            press <UI>Refresh Hours</UI>.
          </Section>
        </>
      ),
    },
    {
      key: 'target',
      label: nonExempt ? 'Target hours, and the projection' : 'Hours to work, and the projection',
      icon: 'bi-calculator',
      body: (
        <>
          <Section title="The left side: where you stand">
            {nonExempt ? (
              <>
                Reads as an equation — <UI>Target hours</UI> minus{' '}
                <UI>Hours worked</UI> leaves what is <UI>Left to work</UI>.
                <List>
                  <li>
                    <UI>Target hours</UI> the number of hours per pay period in
                    your commitment for that period. Non-exempt employees are not held to a TAWH
                    percentage the way exempt staff are. With no target set there
                    is nothing to measure against, and the card shows only the
                    hours you worked.
                  </li>
                  <li>
                    <UI>Hours worked</UI> everything TimeCamp has for the period,
                    paid time off and company close dates included.
                  </li>
                  <li>
                    <UI>Left to work</UI> the difference. Below zero means you
                    are already past your target.
                  </li>
                </List>
              </>
            ) : (
              <>
                Reads as an equation — <UI>Hours to work</UI> minus{' '}
                <UI>Hours worked</UI> leaves what is <UI>Left to work</UI>.
                <List>
                  <li>
                    <UI>Hours to work</UI> your TAWH for the period: its working
                    days (Monday to Friday) at eight hours, at the percentage
                    shown beside the filters, rounded up to the quarter hour.
                    That is the percentage you were on for that period, so an
                    old period still reads as it did after a change.
                    Holidays and company close dates are not taken out — they
                    are part of the period, and a closure is paid back when you
                    log it.
                  </li>
                  <li>
                    <UI>Hours worked</UI> everything TimeCamp has for the period,
                    paid time off and company close dates included.
                  </li>
                  <li>
                    <UI>Left to work</UI> the difference. Below zero means you
                    are already past your hours to work.
                  </li>
                </List>
              </>
            )}
            When the projection is showing, this side is headed{' '}
            <UI>Actual</UI>.
          </Section>

          <Section title="The right side: where you will land">
            Headed <UI>Projected</UI>, and shown only for the current pay period.
            It answers one question: if you work your usual day on every working
            day left and take the time off you have asked for, where do you land
            against your {nonExempt ? 'target' : 'hours to work'}? It reads as
            one sum:
            <List>
              <li>
                <UI>My standard work hours remaining in pay period</UI> every
                working day left, at the length of your working day (the{' '}
                <em>at … hrs a day</em> under the heading). Weekends and company
                close dates are not working days. Today counts in full until you
                log anything against it; after your first entry it adds nothing
                more, because what you worked is already in{' '}
                <UI>Hours worked</UI>.
              </li>
              <li>
                <UI>Company close date</UI> added, not taken away. Each closure
                still to come credits{' '}
                {nonExempt
                  ? 'your recent daily average (your average week over the last four complete weeks, divided by five), rounded up to the whole hour'
                  : 'eight hours at your TAWH percentage, rounded up to the whole hour, so 80% credits 7'}
                . A closure already past that has nothing logged against it yet
                keeps its credit here until it is logged; from then it counts in{' '}
                <UI>Hours worked</UI> instead, never both.
              </li>
              <li>
                <UI>Planned time out</UI> time off entered without a review.
                Filing it that way says no reimbursement is expected, so nothing
                is ever logged for it and the hours are time the period does not
                get back.
              </li>
              <li>
                <UI>Unapproved time off</UI> filed for review and still waiting
                on a decision. Taken off for now; the hours come back the moment
                someone approves it.
              </li>
              <li>
                <UI>PATO hours</UI> approved time off, taken off because it is
                time you will not be at work.
              </li>
              <li>
                <UI>Left to work</UI> taken off last. It is the same number the{' '}
                <UI>Actual</UI> side ends on, and nothing on this side adjusts it.
              </li>
            </List>
            The result is how far over or under you will land. Within half an
            hour either way it reads <UI>on track</UI>.
          </Section>

          <Section title="Reading the figures">
            A figure with an info icon explains its arithmetic when you hover
            over it, and the line under the equation spells out the standard
            hours — what is left today plus the working days after it, times
            your day length.
            <br /><br />
            A warning triangle beside <UI>PATO hours</UI> means TimeCamp has
            nothing against those hours yet. Until it does they count toward
            nothing, so the projection reads that much further under; logging
            the time off moves them into <UI>Hours worked</UI> and the figure
            catches up.
          </Section>

          <Section title="Your working day">
            The day length comes from <UI>Hours on an average working day</UI>,
            set from the gear beside <UI>Projected</UI>. It only changes the
            forecast, never your {nonExempt ? 'target' : 'hours to work'}. The
            first time you open your own timesheet in a current period, the
            projection asks for it — <UI>Set my working day</UI> — rather than
            guessing.
            {nonExempt ? (
              <>
                {' '}The <UI>Total Hours</UI> tile shows your average week over
                the last four complete weeks (part weeks are left out), and the
                gear offers that divided across five days as a suggested day
                length.
              </>
            ) : null}
            <br /><br />
            The same dialog has <UI>Show the projection</UI>. Turned off, the
            card keeps only the <UI>Actual</UI> side, with a{' '}
            <UI>Projection hidden</UI> line and a <UI>Settings</UI> button to
            bring it back.
            {canSelectOthers ? (
              <>
                {' '}On someone else&apos;s timesheet you can set their working
                day but not hide their projection, and if they have never set
                one it assumes eight hours.
              </>
            ) : null}
          </Section>

          <Note title="Reading a range">
            {nonExempt ? (
              <>
                With an <UI>End Pay Period</UI> picked, <UI>Target hours</UI> is
                your per-period target times the number of pay periods in the
                range.
              </>
            ) : (
              <>
                Pick an <UI>End Pay Period</UI> and this card becomes a table of
                hours to work, one row per pay period, each at the commitment in
                force for it. TAWH is rounded per period, so the range is the
                sum of those rows.
              </>
            )}{' '}
            A projection only makes sense against one period, so none is shown
            for a range.
          </Note>
        </>
      ),
    },
    {
      key: 'summary',
      label: 'Summary',
      icon: 'bi-grid-1x2',
      body: (
        <>
          <Section title="">
            <List>
              <li>
                <UI>Total Hours</UI> everything logged in the period, with your
                average per week over the last four complete weeks underneath.
              </li>
              <li>
                <UI>Billable %</UI> the share of those hours logged to billable
                tasks, with the split beneath it.
              </li>
              <li>
                <UI>Time Off</UI> hours booked for the period next to hours
                actually logged against the paid time off task, then each
                request with its status. The two differ for good reasons: people
                are approved for more hours than they end up taking, and time
                off entered without a review is never logged at all.
              </li>
            </List>
          </Section>
        </>
      ),
    },
    {
      key: 'validations',
      label: 'Validations',
      icon: 'bi-check2-square',
      body: (
        <>
          <Section title="What it checks">
            Each row is one rule run over the period's entries. A green check
            means it passed; a red row says how many days or entries failed and opens them
            when you click it, so you can see exactly which entries to fix. An
            amber row opens the same way but is not a failure — it is a day
            worth a look rather than one that is wrong.
            <List>
              <li>
                <strong>30-min meal break every 5 hours</strong> one meal break
                per five hours worked, so a ten-hour day needs two and a
                fifteen-hour day three.
              </li>
              <li>
                <strong>All entries are 15-minute increments</strong> every entry
                must be a multiple of fifteen minutes. Paid breaks are exempt because they are 10 mins.
                An entry only a few seconds off a quarter hour, like 15m 01s, is
                usually the TimeCamp timer stopped a beat late — that is an amber
                warning naming the stray seconds, rather than a failure.
              </li>
              <li>
                <strong>All entries have a description</strong> every entry needs
                one, except markers like meal periods, breaks and paid time off.
              </li>
              <li>
                <strong>Logged to the lowest-level task</strong> time belongs on
                the lowest task in TimeCamp, not on a parent task that has
                subtasks under it. An amber warning that names the subtasks to
                use instead.
              </li>
              <li>
                <strong>No time on archived projects</strong> nothing may be
                logged to a TimeCamp project or task that has been archived.
              </li>
              {nonExempt ? null : (
                <li>
                  <strong>Billable overtime</strong> an amber warning when the
                  billable time across the period comes to more than your TAWH.
                  Going over is allowed — this is a heads-up, not a failure. A
                  task counts as billable when it is marked billable in TimeCamp.
                </li>
              )}
              <li>
                <strong>Approved time off logged</strong> whether time off was
                taken for the correct time on the correct day. Being paid for
                more time off than was approved is the failure; a day logged
                short, not logged yet, or logged against the wrong date is an
                amber warning. Time off entered without a review is not checked
                — nothing is reimbursed, so nothing is logged.
              </li>
              {nonExempt ? (
                <li>
                  <strong>Paid breaks logged</strong> a paid ten-minute break on
                  every day with more than two hours worked, and a second one
                  past six hours.
                </li>
              ) : null}
            </List>
          </Section>

          <Note title="How break time counts">
            A break is not work, so it never counts toward the hours that decide
            how many breaks you owe: five hours worked plus a thirty-minute meal
            is still five hours worked, and owes one meal break rather than two.
            {nonExempt
              ? ' The same goes for paid breaks — a 5h50m day plus a ten-minute break is still a 5h50m day, so it needs one paid break, not two.'
              : ''} Paid time off and close dates are left out of that figure the
            same way.
          </Note>

          <Section title="Reviewer checks">
            Under the automated rules sits a short checklist of judgement calls
            no rule can make — whether the tasks fit the person's role, whether
            billable notes describe the contracted work, whether notes make
            sense for their task. The billable overtime approval question only
            appears when the billable overtime warning has fired. Your reviewer
            ticks these for each pay period, and each tick records who checked
            it and when. Unchecking one keeps that record and adds who unchecked
            it and when. They are not shown when several pay periods are
            selected.
          </Section>

          <Section title="Fixing a failure">
            Open the failing rule, correct the entries in TimeCamp, then come
            back and press <UI>Refresh Hours</UI>. The rule re-runs against the
            fresh data.
          </Section>
        </>
      ),
    },
    {
      key: 'detail',
      label: 'Tasks and entries',
      icon: 'bi-table',
      body: (
        <>
          <Section title="Top Tasks">
            Every task logged in this period, most hours first, with hours and
            how many entries make them up.
          </Section>

          <Section title="Entries">
            Every entry in the period. Click any column heading to sort by it and
            click again to reverse. It opens on the ten most recent, while the button
            under the table shows the rest.
          </Section>
        </>
      ),
    },
  ];

  if (canSelectOthers) {
    chapters.push({
      key: 'others',
      label: "Reading someone else's timesheet",
      icon: 'bi-people',
      body: (
        <>
          <Section title="Pick the person first">
            The <UI>Employee</UI> picker at the left of the filter bar lists the
            people you may review. These are team members you coach, or everyone, depending
            on your permissions. Choosing someone redraws the whole page for
            them: their target, their summary, their validations, their entries.
          </Section>

          <Section title="Refreshing their hours">
            <UI>Refresh Hours</UI> pulls from TimeCamp for whoever is on screen,
            not just for you. This is useful before a review, so you are reading what
            they have logged rather than what was cached earlier.
          </Section>

          <Note title="What you cannot change here">
            Nothing on this page edits anyone's timesheet. Their classification
            and hours target are shown, not set. Those live on the
            employee's profile.
          </Note>
        </>
      ),
    });
  }

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <Modal.Header>
        <Modal.Title>
          <i className="bi bi-question-circle me-2" aria-hidden="true" />
          How to read this page
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {chapters.map((chapter, i) => (
          <Chapter
            key={chapter.key}
            title={chapter.label}
            icon={chapter.icon}
            first={i === 0}
          >
            {chapter.body}
          </Chapter>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onClose}>
          Got it
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
